import { capitalOf } from "../../content/course.ts";
import type { Course } from "../../content/course.ts";
import { itemIdOf, progressKey, type SkillId } from "../../content/types.ts";
import { audioFace, glyphFace } from "../../face.ts";
import type { MatchStep, Option } from "../../lesson.ts";
import { sample, shuffle, uid } from "../../rng.ts";
import { register } from "../registry.ts";
import type { BuildContext } from "../registry.ts";

/**
 * Two columns of the same letters: capitals against lowercase in a caps unit,
 * sounds against lowercase otherwise. Never a Latin reading (see `check`).
 */
export function makeMatch(
	itemIds: readonly string[],
	skill: SkillId,
	size: number,
	course: Course,
	side: "glyph" | "audio" = "glyph",
): MatchStep {
	const ks = sample(itemIds, size);
	const left: Option[] = ks.map((id) => ({
		id: progressKey(id, skill),
		face:
			side === "audio"
				? // The `!` is safe: an audio-side match only builds over recipes whose
					// step requires ["audio"], and the validator refuses a listening step
					// over a speechless item (FEATURE in packages/content/src/validate.ts).
					audioFace(course.itemById(id).audio!)
				: // capitalOf throws on a letter with no capital; same guard as
					// choice's l2u branch and exampleWord's capitals branch.
					glyphFace(skill === "upper" ? capitalOf(id) : course.views.letter(id).l),
	}));
	const right: Option[] = shuffle(
		ks.map((id) => ({ id: progressKey(id, skill), face: glyphFace(course.views.letter(id).l) })),
	);
	return { kind: "match", left, right, uid: uid() };
}

register<MatchStep, string>({
	id: "match",
	appliesTo: ["letter"],
	build(ctx: BuildContext) {
		// See choice/logic.ts's build() for why skill travels via options.
		const skill = ctx.options.skill as SkillId;
		// `options.size` is content-authored; a bad value is caught by this
		// type's `check` comparing `left.length` against the authored size.
		const size = (ctx.options.size as number | undefined) ?? 4;
		const valid = ctx.pool.filter(
			(id) => id !== ctx.itemId && (skill !== "upper" || ctx.course.views.letter(id).u),
		);
		const side = ctx.options.side === "audio" ? "audio" : "glyph";
		return makeMatch([ctx.itemId, ...valid], skill, size, ctx.course, side);
	},
	grade() {
		return true;
	},
	// A match misses through the board's score, its answer the right-hand tile
	// paired with the left one; both columns' ids are progress keys.
	miss: (step, answer) => ({
		picked: itemIdOf(answer),
		shown: step.right.map((o) => itemIdOf(o.id)),
	}),
	graded: false,
	byEar: (cfg) => cfg.options.side === "audio",
	heard: (step) => step.left.some((o) => o.face.kind === "audio"),
	// Left-hand option ids ARE progress keys (see makeMatch above), one per
	// pair on the board — so a match step trains every pair it shows.
	keys: (step) => step.left.map((o) => o.id),
	// Every capitals-unit step draws letters whose `upper` must exist, which
	// is exactly what capitalOf would otherwise throw on at build time.
	requires: (cfg) =>
		cfg.skill === "upper"
			? ["capital"]
			: cfg.skill === "hear" || cfg.options.side === "audio"
				? ["audio"]
				: [],
	check(step, cfg) {
		const out: string[] = [];
		// The same authored `options.size` build() read, and the same cast.
		// It is asserted rather than checked because this line is the check:
		// a `size` that is not a number compares unequal to `left.length` and
		// is reported below as a wrong pair count, rather than passing silently.
		const size = (cfg.options.size as number | undefined) ?? 4;
		if (step.left.length !== size) out.push(`has ${step.left.length} pairs, expected ${size}`);
		if (step.right.length !== step.left.length) out.push("has mismatched columns");
		if (new Set(step.left.map((o) => o.id)).size !== step.left.length)
			out.push("has duplicate pairs");
		// Latin readings are never an answer: a reading tile would train
		// sound → romanization, not the script.
		for (const o of [...step.left, ...step.right])
			if (o.face.kind === "text") out.push(`shows a Latin reading for ${o.id}`);
		// A lowercase match authored without `side: "audio"` pairs each letter
		// with its own glyph, which trains nothing.
		const leftText = new Map(
			step.left.map((o) => [o.id, o.face.kind === "glyph" ? o.face.text : null]),
		);
		if (step.right.some((o) => o.face.kind === "glyph" && leftText.get(o.id) === o.face.text))
			out.push("pairs a letter with itself");
		return out;
	},
});
